import type { LanguageIsoCode } from 'locales';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import type { ProductVariation } from 'models/productsReferential';
import type { UnderlyingCodeType } from 'models/underlying/base';
import type { Region } from 'state/discover/page/page.model';
import type { Width } from 'state/product-description/types';
import type { ProductCodeType } from 'state/secondaryMarket/productSearch';
import type { UserPreferencesState } from 'state/userPreferences/userPreferences.types';
import { WorkspaceWithProductSelector } from 'state/workspaces/workspaces/workspaces.model';

import type { TabsOrderType } from './tabsOrder/tabOrder.model';

export type SgBsTheme = 'dark' | 'light';

export type LandingPage = 'WelcomePage' | 'QuoteWorkspace' | 'EssentialWorkspace';
export type QuoteWorkspaceVersion = 'Standard' | 'Advanced';
export type BatchWorkspaceVersion = 'Standard' | 'Advanced';

export interface Notifications {
  contactIds: string[];
}

export type EssentialExpandedBlocks = Record<
  ProductVariation['productType'],
  Record<ProductVariation['productSubType'], string[]>
>;

export type UserPreferences = Readonly<{
  languageIsoCode: LanguageIsoCode;
  isDarkMode: boolean;
  precision: DisplayPrecisionValues;
  landingPage?: LandingPage;
  discoverPageRegion?: Region;
  quoteWorkspaceVersion?: QuoteWorkspaceVersion;
  batchWorkspaceVersion?: BatchWorkspaceVersion;
  defaultFieldValues: DefaultFieldValues;
  availableSpProducts?: ReadonlyArray<AvailableProduct>;
  availableCurrencies?: ReadonlyArray<string | null>;
  availableDiscoverPageRegions?: ReadonlyArray<Region>;
  underlyingCodeType: UnderlyingCodeType;
  productCodeType?: ProductCodeType;
  closeTabWhenLiveTradeCpOrderIsPlacedActivated: boolean;
  tabsOrder: TabsOrderType | null;
  pinnedProductVariations: readonly ProductVariation[];
  essentialPinnedProductVariations: readonly ProductVariation[];
  notifications?: Notifications;
  showBatchShortcutCheatsheet?: boolean;
  essentialExpandedBlocks?: EssentialExpandedBlocks;
  productLayoutPreferences?: ProductLayoutPreferences;
}>;

export const PRECISIONS = ['fees', 'greeks', 'metrics', 'backtest'] as const;

export type DisplayPrecisionKeys = (typeof PRECISIONS)[number];

export type DisplayPrecisionValues = { [Precision in DisplayPrecisionKeys]?: number };

export interface DefaultFieldValues {
  isPrivateMode: boolean;
  selectedOnBehalfClient?: ISalesCounterpart;
  xMargin?: number;
  productType: string;
  productSubtype: string;
  wrapper: string;
  currency?: string;
}

export type DefaultFieldKeys = keyof DefaultFieldValues;

export interface AvailableProduct {
  productType: string;
  productSubType: string;
  wrapper: string;
}

export const defaultPrecisions = (): DisplayPrecisionValues => ({
  fees: 4,
  greeks: 6,
  metrics: 4,
  backtest: 2,
});

export type ProductVariationKey = string;

export interface ProductLayoutPreferences {
  layoutsByProduct: Record<ProductVariationKey, ProductLayoutOverride>;
}

export interface ProductLayoutOverride {
  visibleFields: FieldId[];
  additionalFields: FieldId[];
  pinnedFields: FieldId[];
  fieldOverrides: Record<FieldId, Width>;
}

export const emptyProductLayoutOverride: ProductLayoutOverride = {
  visibleFields: [],
  additionalFields: [],
  pinnedFields: [],
  fieldOverrides: {},
};

export const emptyProductLayoutPreferences: ProductLayoutPreferences = {
  layoutsByProduct: {},
};

export const initialPreferenceState: UserPreferencesState = {
  status: 'loading',
  precision: defaultPrecisions(),
  productLayoutPreferences: emptyProductLayoutPreferences,
};

export type UserPreferencesPinnedProductsVariationsChangedParams = {
  workspace: WorkspaceWithProductSelector;
  action: 'pin' | 'unpin';
  productVariation: ProductVariation;
};

export interface ProductLayoutFieldPreference {
  fieldId: FieldId;
}

export type FieldId = string;
